<template>
  <div>
    <Field :rules="validEmail" name="email" v-slot="{ field, errors: errs }">
      <TextInput
        :field="field"
        :errors="errs"
        :label="$t('Email')"
        input-class="px-3 py-1.5"
        label-class="w-full"
        direction="flex-col"
      >
        <template #suffix>
          <button
            :disabled="!field.value || otpConfig.isSent"
            class="max-w-max w-full flex items-center gap-x-2 ltr:font-sans text-xs bg-primary/80 hover:bg-primary text-white disabled:bg-brand-gray-600/80 disabled:cursor-not-allowed"
            @click.prevent="sendEmailOtp(field.value, setFieldError)"
          >
            <div class="p-2">
              <faPaperPlane class="w-4 fill-white"></faPaperPlane>
            </div>
          </button>
        </template>
        <template #bottomAppendage>
          <div v-if="otpConfig.isSent" class="w-full flex justify-end">
            {{ otpConfig.otpRemaining }}
          </div>
        </template>
      </TextInput>
    </Field>
    <Modal
      :title="$t('emailVerification')"
      v-model="otpConfig.showVerificationModal"
      container-class="relative w-[24rem] md:w-[32rem]"
    >
      <template #bodyContent>
        <Form v-slot="{ values }">
          <Field :rules="requiredRule" name="otp" v-slot="{ field, errors: errs }">
            <TextInput
              :field="field"
              :errors="errs"
              :label="$t('emailOtpCode')"
              input-class="px-3 py-1.5"
              label-class="w-full"
              direction="flex-col"
            >
              <template #bottomAppendage>
                <span v-if="otpConfig.isSent && otpConfig.timerHalfLife > 0">
                  {{ $t('otpParagraph2', { countdown: otpConfig.timerHalfLife }) }}
                </span>
                <button
                  v-else
                  class="text-start text-primary"
                  @click.prevent="sendEmailOtp(otpConfig.email)"
                >
                  {{ $t('resend') }}
                </button>
              </template>
            </TextInput>
          </Field>
          <div class="pt-2 w-full flex justify-end">
            <button
              class="text-center rounded-md bg-primary/80 hover:bg-primary flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
              @click.prevent="onSubmit(values)"
            >
              <div class="p-2 bg-primary rounded-s-md">
                <faDiskette class="w-4 fill-white"></faDiskette>
              </div>
              <span class="w-full text-center">
                {{ $t('Save') }}
              </span>
              <div class="p-0.5"></div>
            </button>
          </div>
        </Form>
      </template>
      <template #footer>&nbsp;</template>
    </Modal>
  </div>
</template>

<script setup>
import faDiskette from '@/assets/icons/faDiskette.svg?component'
import faPaperPlane from '@/assets/icons/faPaperPlane.svg?component'
import TextInput from '@/components/SimpleForm/TextInput.vue'
import { isValidEmail } from '@/composables/useRuleHandler.js'
import axiosInstance from '@/services'
import { useInterval } from '@vueuse/core'
import { notify } from 'notiwind'
import { Field, Form } from 'vee-validate'
import { reactive, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Modal from '@/components/Ui/Modal/index.vue'

const emit = defineEmits(['onOtp'])
const TIME_LIMIT = 60
const i18n = useI18n()

function requiredRule(value) {
  if (!value || !`${value}`.trim()) {
    return i18n.t('INVALID_REQUIRED')
  }

  return true
}

function validEmail(value) {
  if (!value || !`${value}`.trim()) {
    return i18n.t('INVALID_REQUIRED')
  }

  return isValidEmail(value) || 'INVALID_EMAIL'
}

const otpConfig = reactive({
  isSent: false,
  showOtpTimer: false,
  timerHalfLife: TIME_LIMIT,
  showVerificationModal: false,
  intervalConfig: null,
  watcher: null,
  email: null
})

onMounted(() => {
  otpConfig.intervalConfig = useInterval(1000, { controls: true, immediate: true })

  otpConfig.watcher = watch(
    () => otpConfig.intervalConfig?.counter,
    () => {
      if (otpConfig.timerHalfLife === 0) {
        otpConfig.intervalConfig.reset()
        return
      }
      otpConfig.timerHalfLife -= 1
    }
  )
})

watch(
  () => otpConfig.showVerificationModal,
  (newValue) => {
    if (!newValue) {
      otpConfig.watcher?.()
      otpConfig.isSent = false
      otpConfig.showOtpTimer = false
      otpConfig.timerHalfLife = TIME_LIMIT
      otpConfig.showVerificationModal = false
      otpConfig.intervalConfig = null
      otpConfig.watcher = null
      otpConfig.email = null
    }
  }
)

const sendEmailOtp = async (email, setFieldError) => {
  const isEmailValid = validEmail(email)
  if (isEmailValid != true) {
    setFieldError?.('email', isEmailValid)
    return
  }

  otpConfig.email = email

  const response = await axiosInstance.post('/api/notification/otp/send', { email })

  if (!response || !response?.isSuccess) {
    notify(
      {
        group: 'error',
        title: i18n.t(response.errorMessage)
      },
      6000
    )

    return
  }

  otpConfig.isSent = true
  otpConfig.showVerificationModal = true
  otpConfig.timerHalfLife = TIME_LIMIT
  otpConfig.intervalConfig?.reset?.()
}

const onSubmit = async (form) => {
  otpConfig.watcher?.()
  otpConfig.isSent = false
  otpConfig.showOtpTimer = false
  otpConfig.timerHalfLife = TIME_LIMIT
  otpConfig.showVerificationModal = false
  otpConfig.intervalConfig = null
  otpConfig.watcher = null
  otpConfig.email = null
  emit('onOtp', form.otp)
}
</script>

<style lang="scss" scoped></style>
